<template>
  <div
    class="search-input"
    :class="[
      `search-input--${size}`,
      { 'search-input--active': isActive, 'search-input--disabled': disabled },
    ]"
    :dir="isRtl ? 'rtl' : 'ltr'"
    @click="focusInput"
  >
    <div class="search-input__field">
      <v2-icon
        class="search-input__icon text-gray-400"
        icon="search"
        :size="iconSize"
      />
      <input
        ref="input"
        type="text"
        class="search-input__control"
        :value="localValue"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="localValue = $event.target.value"
        @focus="focused = true"
        @blur="focused = false"
        @keyup.enter="emitSearch"
      />
    </div>

    <div v-if="isActive && !disabled" class="search-input__actions" @click.stop>
      <button
        v-if="hasValue"
        type="button"
        class="search-input__clear"
        :aria-label="clearLabel"
        @mousedown.prevent
        @click="onClear"
      >
        <v2-icon icon="close_small" :size="iconSize" class="text-gray-500" />
      </button>
      <button
        type="button"
        class="search-input__submit"
        @mousedown.prevent
        @click="emitSearch"
      >
        {{ searchLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import V2Icon from 'v2/components/Icon.vue';

export default {
  name: 'SearchInput',
  components: { V2Icon },
  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: 'Search' },
    searchLabel: { type: String, default: 'Search' },
    clearLabel: { type: String, default: 'Clear' },
    size: {
      type: String,
      default: 'medium',
      validator: v => ['medium', 'small'].includes(v),
    },
    disabled: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
  },
  data() {
    return { focused: false, localValue: this.value };
  },
  computed: {
    hasValue() {
      return !!(this.localValue && String(this.localValue).length);
    },
    isActive() {
      return !this.disabled && (this.focused || this.hasValue);
    },
    iconSize() {
      return this.size === 'small' ? 16 : 24;
    },
    isRtl() {
      const rtlLocales = ['he', 'ar', 'fa', 'ur', 'ur_IN'];
      return !!(this.$i18n && rtlLocales.includes(this.$i18n.locale));
    },
  },
  watch: {
    value(val) {
      this.localValue = val;
    },
  },
  mounted() {
    if (this.autofocus) {
      this.$nextTick(this.focusInput);
    }
  },
  methods: {
    focusInput() {
      if (!this.disabled && this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    emitSearch() {
      this.$emit('search', this.localValue);
    },
    onClear() {
      this.localValue = '';
      this.$emit('clear');
      this.$emit('search', '');
      this.$nextTick(this.focusInput);
    },
  },
};
</script>

<style lang="scss" scoped>
.search-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: inset 0 -1px 2px 0 rgba(61, 72, 87, 0.1);
  cursor: text;
  transition: border-color 0.15s ease;

  height: 40px;
  padding-block: 8px;
  padding-inline-start: 12px;
  padding-inline-end: 4px;

  &:hover:not(.search-input--disabled) {
    border-color: #2abb7f;
  }

  &--small {
    height: 32px;
    border-radius: 6px;
    padding-block: 4px;
    padding-inline-start: 8px;
    padding-inline-end: 4px;
  }

  &--disabled {
    background: #f5f5f5;
    border-color: #d4d4d4;
    cursor: not-allowed;
  }
}

.search-input__field {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}

.search-input--small .search-input__field {
  gap: 4px;
}

.search-input__icon {
  flex-shrink: 0;
}

.search-input__control {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  line-height: 22px;
  color: #374151;

  &::placeholder {
    color: #9ca3af;
  }

  &:disabled {
    color: #d4d4d4;
    cursor: not-allowed;
  }
}

.search-input--small .search-input__control {
  font-size: 14px;
  line-height: 18px;
}

.search-input__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.search-input__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.search-input__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 4px 12px;
  border: none;
  border-radius: 5px;
  background: transparent;
  cursor: pointer;
  color: #2abb7f;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  transition: background-color 0.15s ease;

  &:hover {
    background: rgba(42, 187, 127, 0.08);
  }
}

.search-input--small .search-input__submit {
  height: 24px;
  padding: 2px 4px;
}
</style>
